// Section 6: Verification of Analyst Work (certs persist as section 7,
// UW.S7.*).
//
// One verification cert row per non-A1 lane (A2 / A3 / A4). Each row is
// gated until the corresponding LaneAssessment exists. If the lane has
// been flagged Not Applicable via the pill widget, the row auto-completes
// with a "Lane marked Not Applicable" note and no cert is required.

import { useMemo } from "react";
import { Link } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import { Card } from "@/design-system/components/ui";
import { UnderwritingCertificationCheckbox } from "../../common/UnderwritingCertificationCheckbox";
import {
  useDealAssignments,
  useLaneAssessments,
} from "@/hooks/dealFlow/useDealFlow";
import type { CriteriaLane, DealAssignmentDto } from "@/api/dealFlow/types";
import { useDateFormatter } from "@/utils/datetime";

interface VerificationOfAnalystWorkSectionProps {
  dealId: string;
}

const LANES: ReadonlyArray<{ lane: CriteriaLane; code: string; label: string }> = [
  { lane: "A2", code: "UW.S7.VerifyA2", label: "I verify A2's Financial Analysis." },
  { lane: "A3", code: "UW.S7.VerifyA3", label: "I verify A3's Legal Analysis." },
  { lane: "A4", code: "UW.S7.VerifyA4", label: "I verify A4's Business Analysis." },
];

export function VerificationOfAnalystWorkSection({
  dealId,
}: VerificationOfAnalystWorkSectionProps) {
  const assignments = useDealAssignments(dealId);
  const laneAssessments = useLaneAssessments(dealId);
  const { dateTime } = useDateFormatter();

  const assignmentByRole = useMemo(() => {
    const map = new Map<string, DealAssignmentDto>();
    for (const a of assignments.data ?? []) map.set(a.roleCode, a);
    return map;
  }, [assignments.data]);

  const assessmentByLane = useMemo(() => {
    const map = new Map<string, { outcomePassFail: string; dealQualityRating: string; assessedAt: string }>();
    for (const a of laneAssessments.data ?? []) {
      map.set(a.lane, {
        outcomePassFail: a.outcomePassFail,
        dealQualityRating: a.dealQualityRating,
        assessedAt: a.assessedAt,
      });
    }
    return map;
  }, [laneAssessments.data]);

  return (
    <Card>
      <header className="mb-space-3">
        <h3 className="font-display text-h4 text-onyx m-0">
          Section 6 — Verification of Analyst Work
        </h3>
        <p className="text-caption text-gray-mid m-0">
          A1 reviews each analyst lane assessment and signs a verification
          cert. Skipped lanes (marked N/A in Section 3) auto-complete.
        </p>
      </header>

      <ul className="flex flex-col gap-space-3 m-0 p-0 list-none">
        {LANES.map(({ lane, code, label }) => {
          const isNa = assignmentByRole.get(lane)?.status === "NotApplicable";
          const assessment = assessmentByLane.get(lane);
          const tabHref = `/deal-flow/deals/${dealId}?tab=${tabSlugForLane(lane)}`;

          return (
            <li
              key={lane}
              className="border border-border-light rounded-md p-space-3 bg-white-pure"
            >
              <div className="flex items-start justify-between gap-space-3">
                <div className="flex-1 min-w-0">
                  <div className="font-display text-label uppercase tracking-label text-gold-shadow mb-space-1">
                    {lane}
                  </div>
                  <div className="text-small text-onyx">
                    {isNa ? (
                      <em className="text-gray-mid not-italic">
                        Lane marked Not Applicable — verification skipped.
                      </em>
                    ) : assessment ? (
                      <>
                        Issued <strong>{assessment.outcomePassFail}</strong> with
                        DQR {assessment.dealQualityRating} on{" "}
                        {dateTime(assessment.assessedAt)}
                      </>
                    ) : (
                      <em className="text-gray-mid not-italic">
                        Awaiting {lane} Pass/Fail issuance.
                      </em>
                    )}
                  </div>
                </div>
                <Link
                  to={tabHref}
                  className="inline-flex items-center gap-space-1 text-label text-gold-shadow hover:underline whitespace-nowrap"
                >
                  <ExternalLink size={12} aria-hidden /> View {lane} tab
                </Link>
              </div>
              {!isNa && (
                <div className="mt-space-2">
                  <UnderwritingCertificationCheckbox
                    dealId={dealId}
                    tab="Underwriting"
                    section={7}
                    certificationCode={code}
                    label={label}
                    disabled={!assessment}
                    disabledReason={
                      assessment
                        ? undefined
                        : `${lane} has not issued a lane Pass/Fail yet.`
                    }
                  />
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

function tabSlugForLane(lane: CriteriaLane): string {
  switch (lane) {
    case "A2":
      return "financial-analysis";
    case "A3":
      return "legal-analysis";
    case "A4":
      return "business-analysis";
    default:
      return "underwriting";
  }
}
